import { useEffect, useState } from 'react'
import { api } from '@/util/api'
import { AdminLayout } from '@/components/admin/AdminLayout'
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogClose,
  EmptyState,
  Form,
  IconButton,
  Notice,
  Select,
  Skeleton,
  SkeletonGroup,
  TextField,
} from '@/components/play'
import { Plus, Pencil, Trash2, Users } from 'lucide-react'
import { useAdminOrganizations } from '@/hooks/useAdminOrganizations'
import type { TeamResource } from '@/types/admin'

export function AdminTeamsPage() {
  const [teams, setTeams] = useState<TeamResource[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingTeam, setEditingTeam] = useState<TeamResource | null>(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [orgFilter, setOrgFilter] = useState<string>('all')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const { organizations, isSuperadmin } = useAdminOrganizations()

  useEffect(() => {
    loadTeams()
  }, [orgFilter])

  async function loadTeams() {
    setLoading(true)
    setLoadFailed(false)
    try {
      const params: Record<string, string> = {}
      if (orgFilter !== 'all') params.organization_id = orgFilter
      const res = await api.getAdminTeams(params)
      setTeams(res.data)
    } catch {
      setLoadFailed(true)
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setEditingTeam(null)
    setFormName('')
    setFormDescription('')
    setError(null)
    setShowForm(true)
  }

  function openEdit(team: TeamResource) {
    setEditingTeam(team)
    setError(null)
    setFormName(team.attributes.name)
    setFormDescription(team.attributes.description || '')
    setShowForm(true)
  }

  async function handleSubmit() {
    setError(null)

    if (!editingTeam && isSuperadmin && orgFilter === 'all') {
      setError('Choose an organization before creating a team.')
      return
    }

    setSaving(true)
    try {
      if (editingTeam) {
        await api.updateAdminTeam(editingTeam.id, { name: formName, description: formDescription })
      } else {
        await api.createAdminTeam({
          name: formName,
          description: formDescription,
          organization_id: isSuperadmin ? orgFilter : undefined,
        })
      }
      setShowForm(false)
      loadTeams()
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'We couldn’t save the team. Try again.')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(team: TeamResource) {
    await api.deleteAdminTeam(team.id)
    loadTeams()
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="text-3xl font-semibold">Teams</h1>
          <Button size="compact" onClick={openCreate}>
            <Plus /> New team
          </Button>
        </div>

        {isSuperadmin && organizations.length > 0 && (
          <div className="flex gap-2">
            <Select
              className="w-[180px]"
              label="Organization"
              items={[
                { value: 'all', label: 'All organizations' },
                ...organizations.map((org) => ({ value: org.id, label: org.attributes.name })),
              ]}
              value={orgFilter}
              onValueChange={(v) => setOrgFilter(v ?? 'all')}
            />
          </div>
        )}

        {loading ? (
          <SkeletonGroup label="Loading teams" className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} shape="panel" className="h-32" />)}
          </SkeletonGroup>
        ) : loadFailed ? (
          <Notice tone="error" action={<Button variant="secondary" size="compact" onClick={() => void loadTeams()}>Try again</Button>}>
            We couldn’t load teams.
          </Notice>
        ) : teams.length === 0 ? (
          // New team leads the page, so the empty state doesn't repeat it.
          <EmptyState
            icon={<Users />}
            title="No teams yet"
            description="Teams group people within an organization."
          />
        ) : (
          <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {teams.map((team) => (
              <Card
                key={team.id}
                as="li"
                title={team.attributes.name}
                description={team.attributes.description || undefined}
                actions={
                  <>
                    <IconButton label={`Edit ${team.attributes.name}`} variant="ghost" size="compact" onClick={() => openEdit(team)}>
                      <Pencil />
                    </IconButton>
                    <ConfirmDialog
                      trigger={
                        <IconButton label={`Delete ${team.attributes.name}`} variant="ghost" size="compact">
                          <Trash2 />
                        </IconButton>
                      }
                      title={`Delete team "${team.attributes.name}"?`}
                      confirmLabel="Delete team"
                      onConfirm={() => handleDelete(team)}
                    />
                  </>
                }
              >
                <div className="flex items-center gap-2">
                  <Users size={14} className="text-neutral-7" aria-hidden="true" />
                  <span>{team.attributes.member_count} {team.attributes.member_count === 1 ? 'member' : 'members'}</span>
                  {!team.attributes.active && (
                    <Badge tone="quiet" className="ml-auto">Inactive</Badge>
                  )}
                </div>
              </Card>
            ))}
          </ul>
        )}

        <Dialog
          open={showForm}
          onOpenChange={setShowForm}
          title={editingTeam ? 'Edit team' : 'New team'}
          footer={
            <>
              <DialogClose>Cancel</DialogClose>
              <Button type="submit" form="admin-team-form" loading={saving}>{editingTeam ? 'Save' : 'Create team'}</Button>
            </>
          }
        >
          <Form id="admin-team-form" className="space-y-4" onFormSubmit={() => void handleSubmit()}>
            {error && <Notice tone="error" alert>{error}</Notice>}
            <TextField
              name="name"
              label="Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              required
              requiredMessage="Name the team."
            />
            <TextField name="description" label="Description" optional value={formDescription} onChange={(e) => setFormDescription(e.target.value)} />
          </Form>
        </Dialog>
      </div>
    </AdminLayout>
  )
}
